/**
 * APTableBody Component
 *
 * Table body section that manages data rows, empty states, and loading states.
 * Orchestrates the rendering of row content based on data availability and loading state.
 *
 * @example
 * ```tsx
 * <APTableBody
 *   columns={columns}
 *   rows={dataRows}
 *   loading={false}
 *   emptyState={{ title: 'No data', description: 'No records found' }}
 *   onRowClick={(row) => console.log(row)}
 * />
 * ```
 */

import { forwardRef } from 'react';

import type {
  IAPTableColumn,
  IAPTableEmptyState,
  IAPTableLoadingState,
  IAPTableRow,
  IAPTableSelection,
} from '../../types';

import APTableEmptyState from '../empty-state';
import APTableLoadingState from '../loading-state';
import APTableRow from '../row';

import './styles.scss';

export interface IAPTableBodyProps<TData = Record<string, unknown>> {
  /**
   * Table columns configuration
   */
  columns: IAPTableColumn<TData>[];

  /**
   * Table data rows
   */
  rows: IAPTableRow<TData>[];

  /**
   * Table selection configuration
   */
  selection?: IAPTableSelection;

  /**
   * Empty state configuration
   */
  emptyState?: IAPTableEmptyState;

  /**
   * Loading state configuration
   */
  loadingState?: IAPTableLoadingState;

  /**
   * Row click callback
   */
  onRowClick?: (row: IAPTableRow<TData>, rowIndex: number) => void;

  /**
   * Row double click callback
   */
  onRowDoubleClick?: (row: IAPTableRow<TData>, rowIndex: number) => void;

  /**
   * Additional CSS class names
   */
  className?: string;

  /**
   * Test identifier for testing
   */
  'data-testid'?: string;
}

/**
 * APTableBody - Table body section
 *
 * Manages data rows and handles empty/loading states based on
 * data availability and loading status.
 */
export const APTableBody = forwardRef<HTMLTableSectionElement, IAPTableBodyProps>(
  <TData,>(
    {
      columns,
      rows,
      selection,
      emptyState,
      loadingState,
      onRowClick,
      onRowDoubleClick,
      className = '',
      'data-testid': testId = 'ap-table-body',
      ...rest
    }: IAPTableBodyProps<TData>,
    ref: React.Ref<HTMLTableSectionElement>
  ) => {
    // Build body classes
    const bodyClasses = ['ap-table__body', className].filter(Boolean).join(' ');

    // Filter visible columns for column span calculations
    const visibleColumns = columns.filter((column) => column.visible !== false);
    const columnCount =
      visibleColumns.length +
      (selection?.type !== 'none' && selection?.showCheckboxes !== false ? 1 : 0);

    return (
      <tbody ref={ref} className={bodyClasses} data-testid={testId} {...rest}>
        {/* Loading state */}
        {loadingState?.loading ? (
          <APTableLoadingState
            loading={loadingState.loading}
            content={loadingState.content}
            columns={columns as IAPTableColumn[]}
          />
        ) : rows.length === 0 ? (
          /* Empty state */
          <tr className="ap-table__empty-row">
            <td colSpan={columnCount} className="ap-table__empty-cell">
              <APTableEmptyState
                title={emptyState?.title}
                description={emptyState?.description}
                icon={emptyState?.icon}
                content={emptyState?.content}
                action={emptyState?.action}
              />
            </td>
          </tr>
        ) : (
          /* Data rows */
          rows.map((row, index) => (
            <APTableRow
              key={row.id}
              row={row}
              columns={columns}
              rowIndex={index}
              selection={selection}
              onRowClick={onRowClick}
              onRowDoubleClick={onRowDoubleClick}
            />
          ))
        )}
      </tbody>
    );
  }
) as <TData = Record<string, unknown>>(
  props: IAPTableBodyProps<TData> & {
    ref?: React.Ref<HTMLTableSectionElement>;
  }
) => React.ReactElement;

export default APTableBody;
